import { useState } from "react";
import { ChevronDown, ChevronRight, AlertTriangle, MapPin, FileDown, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FLAG_VALUES = new Set(["no", "fail"]);
const HAS_BUTTONS = new Set(["yes_no", "yes_no_na", "pass_fail", "pass_fail_na", "na_ok"]);

export function InspectionReportInline({ data }) {
  const [expandedLocs, setExpandedLocs] = useState({});
  const insp = data;
  const locations = insp.locations || [];

  let totalItems = 0, flaggedItems = 0;
  const flaggedList = [];
  locations.forEach(loc => (loc.responses || []).forEach(r => {
    totalItems++;
    if (r.flagged) {
      flaggedItems++;
      flaggedList.push({ ...r, locName: loc.name, locId: loc.identifier });
    }
  }));
  const passRate = totalItems ? Math.round(((totalItems - flaggedItems) / totalItems) * 100) : 0;

  return (
    <div className="border-t bg-slate-50/50 p-4 space-y-4" data-testid="inline-inspection-report">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border text-center p-3">
          <div className="text-xl font-bold">{locations.length}</div>
          <div className="text-[10px] text-slate-500">Locations</div>
        </div>
        <div className="bg-white rounded-lg border text-center p-3">
          <div className="text-xl font-bold">{totalItems}</div>
          <div className="text-[10px] text-slate-500">Items Checked</div>
        </div>
        <div className="bg-white rounded-lg border text-center p-3 bg-red-50/50">
          <div className="text-xl font-bold text-red-600">{flaggedItems}</div>
          <div className="text-[10px] text-red-500">Flagged</div>
        </div>
        <div className="bg-white rounded-lg border text-center p-3 bg-emerald-50/50">
          <div className="text-xl font-bold text-emerald-600">{passRate}%</div>
          <div className="text-[10px] text-emerald-500">Pass Rate</div>
        </div>
      </div>

      {/* Summary */}
      {(insp.custom_summary || insp.summary_notes) && (
        <div className="bg-white rounded-lg border p-3">
          <h5 className="text-xs font-semibold text-slate-500 uppercase mb-1">Summary</h5>
          <p className="text-sm text-slate-600 whitespace-pre-line">{insp.custom_summary || insp.summary_notes}</p>
        </div>
      )}

      {/* Flagged Items */}
      {flaggedList.length > 0 && (
        <div className="bg-white rounded-lg border p-3">
          <h5 className="text-xs font-semibold text-red-600 uppercase flex items-center gap-1 mb-2"><AlertTriangle className="w-3.5 h-3.5" />Flagged Items ({flaggedList.length})</h5>
          <div className="space-y-2">
            {flaggedList.map((item) => (
              <div key={`flagged-${item.locId || item.locName}-${item.checkpoint_label}`} className="border border-red-100 rounded p-2 bg-red-50/50">
                <div className="text-[10px] text-red-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{item.locName}{item.locId ? ` (${item.locId})` : ""}</div>
                <p className="text-sm font-medium text-slate-800 mt-0.5">{item.checkpoint_label}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className="bg-red-100 text-red-600 border-0 text-[10px]">{item.value === "no" ? "No" : item.value === "fail" ? "Fail" : item.value}</Badge>
                  {item.notes && <span className="text-xs text-slate-500 italic">"{item.notes}"</span>}
                </div>
                {item.photos?.length > 0 && (
                  <div className="flex gap-1.5 mt-1.5">{item.photos.map((p) => <img key={`photo-${p}`} src={`${BACKEND_URL}${p}`} className="w-14 h-14 object-cover rounded border" alt="" />)}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Location Details */}
      <div className="bg-white rounded-lg border p-3">
        <h5 className="text-xs font-semibold text-slate-500 uppercase mb-2">Inspection Detail</h5>
        <div className="space-y-1.5">
          {locations.map(loc => {
            const locFlagged = (loc.responses || []).filter(r => r.flagged).length;
            const expanded = expandedLocs[loc.id];
            return (
              <div key={loc.id} className="border rounded overflow-hidden">
                <button className="w-full flex items-center justify-between p-2 hover:bg-slate-50 text-left" onClick={() => setExpandedLocs(p => ({ ...p, [loc.id]: !p[loc.id] }))}>
                  <div className="flex items-center gap-2">
                    {expanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                    <span className="font-medium text-sm">{loc.name}</span>
                    {loc.identifier && <span className="text-xs text-slate-400">({loc.identifier})</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {locFlagged > 0 && <Badge className="bg-red-100 text-red-600 border-0 text-[10px]">{locFlagged} flagged</Badge>}
                    <span className="text-[10px] text-slate-400">{loc.responses?.length || 0} items</span>
                  </div>
                </button>
                {expanded && (
                  <div className="border-t px-3 pb-2 space-y-1 pt-1.5">
                    {loc.overall_photos?.length > 0 && (
                      <div className="flex gap-1.5 mb-2">{loc.overall_photos.map((p) => <img key={`oph-${p}`} src={`${BACKEND_URL}${p}`} className="w-16 h-16 object-cover rounded border" alt="" />)}</div>
                    )}
                    {(loc.responses || []).map((r, ri) => (
                      // eslint-disable-next-line react/no-array-index-key -- responses keep stable order; checkpoint_label can collide so ri is the unique discriminator
                      <div key={`resp-${ri}`} className={`flex items-start gap-2 p-1.5 rounded text-sm ${r.flagged ? "bg-red-50 border border-red-100" : ""}`}>
                        <span className="text-[10px] text-slate-400 font-mono w-4 mt-0.5">{ri + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-slate-700 text-sm">{r.checkpoint_label}</p>
                          {(r.rating_type === "long_text" || r.rating_type === "text") && r.value && <p className="text-xs text-slate-500 mt-0.5">{r.value}</p>}
                          {r.notes && <p className="text-xs text-slate-400 mt-0.5 italic">"{r.notes}"</p>}
                          {r.photos?.length > 0 && (
                            <div className="flex gap-1 mt-1">{r.photos.map((p) => <img key={`rp-${p}`} src={`${BACKEND_URL}${p}`} className="w-10 h-10 object-cover rounded border" alt="" />)}</div>
                          )}
                        </div>
                        {HAS_BUTTONS.has(r.rating_type) && r.value ? (
                          <Badge className={`border-0 text-[10px] flex-shrink-0 ${FLAG_VALUES.has(r.value) ? "bg-red-100 text-red-600" : r.value === "na" ? "bg-slate-100 text-slate-500" : "bg-emerald-100 text-emerald-600"}`}>
                            {r.value.charAt(0).toUpperCase() + r.value.slice(1)}
                          </Badge>
                        ) : !HAS_BUTTONS.has(r.rating_type) ? null : (
                          <Badge className="bg-slate-100 text-slate-400 border-0 text-[10px]">—</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(`/inspections/${insp.id}`, '_blank')} data-testid={`view-full-report-${insp.id}`}>
          <FileDown className="w-3.5 h-3.5 mr-1" />Full Report
        </Button>
      </div>
    </div>
  );
}
